/**
 * ClickToEditAdd Field Renderer
 *
 * Click-to-edit/add inline form
 * Displays data in a compact view with an Edit/Add button
 * Clicking the button reveals an inline form for editing or adding data
 */

import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';
import type { FormField } from '@/MyCode/components/JsonFormSteps';

/**
 * ClickToEditAdd Renderer
 * Features:
 * - Compact display mode with Edit/Add button
 * - Inline edit form mode
 * - Support for text, textarea, and select fields
 * - Customizable display fields
 * - Configurable button text
 * - Save/Cancel actions
 */
export const renderClickToEditAdd: FieldRenderer = ({ field, value, hasError, handleChange, isRequired, formData }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState<Record<string, any>>({});

    // Initialize edit data when entering edit mode
    const handleEditClick = () => {
        if (value && typeof value === 'object') {
            setEditData(value);
        } else {
            // Initialize with empty values for add mode
            const initialData: Record<string, any> = {};
            field.ui?.editFields?.forEach(editField => {
                initialData[editField.name] = '';
            });
            setEditData(initialData);
        }
        setIsEditing(true);
    };

    // Handle save action
    const handleSave = () => {
        handleChange(field.name, editData);
        setIsEditing(false);
    };

    // Handle cancel action
    const handleCancel = () => {
        setIsEditing(false);
        setEditData({});
    };

    // Handle field changes in edit mode
    const handleEditFieldChange = (fieldName: string, fieldValue: any) => {
        setEditData(prev => ({
            ...prev,
            [fieldName]: fieldValue
        }));
    };

    // Determine if we have data to display
    const hasData = value && typeof value === 'object' && Object.keys(value).length > 0;
    const buttonText = field.ui?.buttonText || (hasData ? 'Edit' : 'Add');

    return (
        <div className="space-y-2">
            <Label>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>

            {!isEditing ? (
                // Display mode
                <div className={`flex items-center justify-between p-4 border rounded-lg ${hasError ? 'border-destructive' : 'border-input'} bg-background`}>
                    <div className="flex-1">
                        {hasData ? (
                            // Display existing data
                            <div className="space-y-1">
                                {field.ui?.displayFields?.map(fieldName => {
                                    const displayValue = value[fieldName];
                                    if (displayValue !== undefined && displayValue !== null && displayValue !== '') {
                                        return (
                                            <div key={fieldName} className="text-sm">
                                                <span className="font-medium text-muted-foreground capitalize">
                                                    {fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                                                </span>
                                                <span className="ml-2">{displayValue}</span>
                                            </div>
                                        );
                                    }
                                    return null;
                                }) || (
                                    <div className="text-sm text-muted-foreground">
                                        {Object.entries(value).map(([key, val]) => (
                                            <div key={key}>
                                                <span className="font-medium capitalize">
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                                                </span>
                                                <span className="ml-2">{String(val)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            // No data placeholder
                            <div className="text-sm text-muted-foreground">
                                {field.placeholder || 'No data added yet'}
                            </div>
                        )}
                    </div>

                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleEditClick}
                        className="ml-4"
                    >
                        <LucideIcons.Edit className="w-4 h-4 mr-1" />
                        {buttonText}
                    </Button>
                </div>
            ) : (
                // Edit mode
                <Card className={hasError ? 'border-destructive' : ''}>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">
                            {hasData ? 'Edit' : 'Add'} {field.label}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {field.ui?.editFields?.map(editField => {
                            const editValue = editData[editField.name] || '';
                            const editHasError = false; // You can implement validation here if needed

                            return (
                                <div key={editField.name} className="space-y-2">
                                    <Label htmlFor={`edit-${editField.name}`}>
                                        {editField.label}
                                        {editField.required && <span className="text-destructive ml-1">*</span>}
                                    </Label>

                                    {editField.type === 'textarea' ? (
                                        <Textarea
                                            id={`edit-${editField.name}`}
                                            placeholder={editField.placeholder}
                                            value={editValue}
                                            onChange={(e) => handleEditFieldChange(editField.name, e.target.value)}
                                            maxLength={editField.maxLength}
                                            className={editHasError ? 'border-destructive' : ''}
                                        />
                                    ) : editField.type === 'select' ? (
                                        <Select
                                            value={editValue}
                                            onValueChange={(val) => handleEditFieldChange(editField.name, val)}
                                        >
                                            <SelectTrigger className={editHasError ? 'border-destructive' : ''}>
                                                <SelectValue placeholder={editField.placeholder || 'Select an option'} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {editField.options?.items?.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Input
                                            id={`edit-${editField.name}`}
                                            type={editField.type || 'text'}
                                            placeholder={editField.placeholder}
                                            value={editValue}
                                            onChange={(e) => handleEditFieldChange(editField.name, e.target.value)}
                                            maxLength={editField.maxLength}
                                            min={editField.minValue}
                                            max={editField.maxValue}
                                            step={editField.step}
                                            className={editHasError ? 'border-destructive' : ''}
                                        />
                                    )}
                                </div>
                            );
                        })}

                        <div className="flex justify-end space-x-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleCancel}
                            >
                                <LucideIcons.X className="w-4 h-4 mr-1" />
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                onClick={handleSave}
                            >
                                <LucideIcons.Check className="w-4 h-4 mr-1" />
                                Save
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default renderClickToEditAdd;
